/**
 * Helper functions to safely access environment variables
 * This helps with Vite's special handling of environment variables
 */

// Define interface for window with __ENV property
interface WindowWithEnv extends Window {
  __ENV?: Record<string, string>;
}

// Get environment variables with appropriate fallbacks
export const getEnv = (key: string, fallback: string = ''): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Handle Vite environment
    return (import.meta.env[key] as string) || fallback;
  } else if (typeof process !== 'undefined' && process.env) {
    // Handle Node.js environment
    return process.env[key] || fallback;
  } else if (typeof window !== 'undefined') {
    // Handle environment with injected variables
    const windowWithEnv = window as WindowWithEnv;
    return windowWithEnv.__ENV?.[key] || fallback;
  }
  return fallback;
};

// Get PostHog configuration
export const getPostHogConfig = () => {
  // Prioritize the recommended environment variable names
  const apiKey = 
    getEnv('VITE_PUBLIC_POSTHOG_KEY') || 
    getEnv('VITE_POSTHOG_API_KEY') ||
    getEnv('POSTHOG_API_KEY') ||
    getEnv('REACT_APP_POSTHOG_API_KEY');

  const apiHost = 
    getEnv('VITE_PUBLIC_POSTHOG_HOST') || 
    getEnv('VITE_POSTHOG_HOST') ||
    getEnv('POSTHOG_HOST') ||
    getEnv('REACT_APP_POSTHOG_HOST') ||
    'https://app.posthog.com';

  // Log which values are being used
  console.log('[PostHog Config] Environment variables check:', {
    vitePublicPosthogKey: !!getEnv('VITE_PUBLIC_POSTHOG_KEY'),
    vitePosthogApiKey: !!getEnv('VITE_POSTHOG_API_KEY'),
    vitePublicPosthogHost: !!getEnv('VITE_PUBLIC_POSTHOG_HOST'),
    vitePosthogHost: !!getEnv('VITE_POSTHOG_HOST'),
    selectedHost: apiHost,
    keyLength: apiKey?.length || 0
  });

  return {
    apiKey,
    apiHost,
    hasKey: !!apiKey,
    debug: {
      checked: [
        'VITE_PUBLIC_POSTHOG_KEY',
        'VITE_POSTHOG_API_KEY',
        'POSTHOG_API_KEY',
        'REACT_APP_POSTHOG_API_KEY'
      ],
      apiKeyExists: !!apiKey,
      apiKeyLength: apiKey?.length || 0,
    }
  };
}; 